import HttpRequest from "@/util/HttpRequest";
import constant from "@/constant/constant";
import {convertToQueryString} from "@/util/DataUtil";

// 중복 요청 방지를 위한 진행 중인 요청 저장소
const pendingRequests = new Map();

// 메모리 캐시 저장소 (목록 요청용)
const memoryCache = new Map();

// 캐시 만료 시간 (10초)
const CACHE_DURATION = 10 * 1000;

// 캐시 키 생성 함수
function generateCacheKey(url, params = {}) {
  const paramString = JSON.stringify(params);
  return `${url}:${paramString}`;
}

// 캐시 확인 함수
function getCachedData(cacheKey) {
  const cached = memoryCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  if (cached) {
    memoryCache.delete(cacheKey);
  }
  return null;
}

// 캐시 저장 함수
function setCachedData(cacheKey, data) {
  memoryCache.set(cacheKey, {
    data,
    timestamp: Date.now()
  });
}

// 중복 요청 방지 함수
function handleRequest(requestKey, requestFn, useCache = false) {
  // 캐시 확인 (목록 요청인 경우)
  if (useCache) {
    const cachedData = getCachedData(requestKey);
    if (cachedData) {
      return Promise.resolve(cachedData);
    }
  }

  // 진행 중인 요청이 있는지 확인
  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }

  // 새로운 요청 생성
  const promise = requestFn()
    .then((response) => {
      // 캐시 저장 (목록 요청인 경우)
      if (useCache) {
        setCachedData(requestKey, response);
      }
      return response;
    })
    .finally(() => {
      // 요청 완료 후 pendingRequests에서 제거
      pendingRequests.delete(requestKey);
    });

  // 진행 중인 요청으로 등록
  pendingRequests.set(requestKey, promise);

  return promise;
}

export default {
  async LOAD_BOARD({ commit, state }, params) {
    const boardId = params.boardId;

    if (!state.boardData || state.boardData.board_slug !== boardId) {
      const requestKey = `LOAD_BOARD:${boardId}`;

      return handleRequest(requestKey, () => {
        return new Promise((resolve, reject) => {
          HttpRequest.get(`/api/v1/board/${boardId}`)
            .then((response) => {
              commit("LOAD_BOARD", response);
              resolve(response);
            })
            .catch((err) => reject(err));
        });
      }, false); // 상세 정보 조회이므로 캐시 미적용
    }
  },

  /**
   * 게시판 목록
   * @params {page, perPage}
   */
  async LOAD_BOARD_LIST({ commit }) {
    const requestKey = 'LOAD_BOARD_LIST';

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.get("/api/v1/board")
          .then((response) => {
            // mutations 메소드 호출
            commit("LOAD_BOARD_LIST", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, true); // 목록 요청이므로 캐시 적용
  },

  /**
   * 게시판 종류 불러오기
   * @params {page, perPage}
   */
  async LOAD_BOARD_TYPE_LIST({ commit }) {
    const requestKey = 'LOAD_BOARD_TYPE_LIST';

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.get("/api/v1/info/board/type")
          .then((response) => {
            // mutations 메소드 호출
            commit("LOAD_BOARD_TYPE_LIST", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, true); // 목록 요청이므로 캐시 적용
  },

  // 공지사항 게시글 불러오기
  async LOAD_POST_LIST({ commit }, params) {
    const boardId = params.boardId;
    const option = {
      params: {
        projectId: params.projectId,
        page: params.page,
        perPage: params.perPage,
        keyword: params.keyword,
        preview: params.preview,
        dateTarget: params.dateTarget,
        dateBegin: params.dateBegin,
        dateEnd: params.dateEnd,
        orderTarget: params.orderTarget,
        orderDirection: params.orderDirection,
        companyIds: params.companyIds,
      },
    };

    // conditions가 있는 경우 convertToQueryString으로 변환하여 params에 추가
    if (params.conditions && params.conditions.length > 0) {
      const conditionParams = new URLSearchParams(convertToQueryString(params.conditions));
      conditionParams.forEach((value, key) => {
        option.params[key] = value;
      });
    }

    const requestKey = generateCacheKey(`LOAD_POST_LIST:${boardId}`, option.params);

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.get(`/api/v1/board/post/${boardId}`, option)
          .then((response) => {
            commit("LOAD_POST_LIST", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, true); // 목록 요청이므로 캐시 적용
  },

  async LOAD_POST({ commit }, params) {
    const boardId = params.boardId;
    const postId = params.postId;
    const requestKey = `LOAD_POST:${boardId}:${postId}`;

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.get(`/api/v1/board/post/${boardId}/${postId}`)
          .then((response) => {
            commit("LOAD_POST", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, false); // 상세 정보 조회이므로 캐시 미적용
  },

  async LOAD_SUMMARY_POST_COUNT({ commit }, params) {
    const option = {
      params: {
        projectId: params.projectId,
        boardIds: params.boardIds,
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
        dateTarget: params.dateTarget,
        companyIds: params.companyIds,
        fieldIds: params.fieldIds,
        statusIds: params.statusIds,
      }
    }
    return new Promise((resolve, reject) => {
      HttpRequest.get(`/api/v1/summary/board/count`, option)
        .then((response) => {
          commit("LOAD_POST", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  /**
   * 361 Viewer.vue 페이지에서 사용한다
   * @param commit
   * @param params
   * @returns {Promise<unknown>}
   * @constructor
   */
  // eslint-disable-next-line no-unused-vars
  async LOAD_361_SUMMARY({ commit }, params) {
    const option = {
      params: {
        projectId: params.projectId,
        targetYear: params.targetYear,
        targetMonth: params.targetMonth,
        fieldId: params.fieldId,
        companyIds: params.companyIds,
      },
    };

    const requestKey = generateCacheKey('LOAD_361_SUMMARY', option.params);

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.get(`/api/v1/summary/post/361`, option)
          .then((response) => {
            // commit("LOAD_361_SUMMARY", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, true); // 요약 데이터 목록이므로 캐시 적용
  },

  async LOAD_COMMENT_LIST({ commit }, params) {
    const boardSlug = params.boardSlug;
    const postId = params.postId;
    const requestKey = `LOAD_COMMENT_LIST:${boardSlug}:${postId}`;

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.get(`/api/v1/board/comment/${boardSlug}/${postId}`)
          .then((response) => {
            commit("LOAD_COMMENT_LIST", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, true); // 댓글 목록이므로 캐시 적용
  },

  async DELETE_POST({ commit }, params) {
    const boardId = params.boardId;
    const postId = params.postId;
    const requestKey = `DELETE_POST:${boardId}:${postId}`;

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.delete(`/api/v1/board/post/${boardId}/${postId}`)
          .then((response) => {
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, false); // DELETE 요청이므로 캐시 미적용
  },

  async CREATE_POST({ commit }, params) {
    const option = {
      title: params.title,
      content: params.content,
      parentId: params.parentId,
      uploads: params.uploads,
      projectId: params.projectId,
      projectOrderId: params.projectOrderId, //orderId를 전송할 경우 반드시 projectId도 같이 보내야 한다.
    };

    const boardId = params.boardId;
    const requestKey = `CREATE_POST:${boardId}:${Date.now()}`;

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.put(`/api/v1/board/post/${boardId}/`, option)
          .then((response) => {
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, false); // POST 요청이므로 캐시 미적용
  },

  async UPDATE_POST({ commit }, params) {
    const option = {
      title: params.title,
      content: params.content,
      uploads: params.uploads,
      projectId: params.projectId,
      projectOrderId: params.projectOrderId, //orderId를 전송할 경우 반드시 projectId도 같이 보내야 한다.
    };

    const boardId = params.boardId;
    const postId = params.postId;
    const requestKey = `UPDATE_POST:${boardId}:${postId}:${Date.now()}`;

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.post(`/api/v1/board/post/${boardId}/${postId}`, option)
          .then((response) => {
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, false); // POST 요청이므로 캐시 미적용
  },

  async ADD_COMMENT({ commit }, params) {
    const option = {
      parentId: params.parentId,
      content: params.comment,
    };

    // 부모 댓글 ID가 없을 경우 변수 삭제
    if (!option.parentId || option.parentId === 0 || option.parentId === "0") {
      delete option.parentId;
    }

    const boardSlug = params.boardSlug;
    const postId = params.postId;
    const requestKey = `ADD_COMMENT:${boardSlug}:${postId}:${Date.now()}`;

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.put(`/api/v1/board/comment/${boardSlug}/${postId}`, option)
          .then((response) => {
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, false); // POST 요청이므로 캐시 미적용
  },

  async UPDATE_COMMENT({ commit }, params) {
    const option = {
      content: params.comment,
    };

    const boardSlug = params.boardSlug;
    const postId = params.postId;
    const commentId = params.commentId;
    const requestKey = `UPDATE_COMMENT:${boardSlug}:${postId}:${commentId}:${Date.now()}`;

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.post(`/api/v1/board/comment/${boardSlug}/${postId}/${commentId}`, option)
          .then((response) => {
            commit("UPDATE_COMMENT", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, false); // POST 요청이므로 캐시 미적용
  },

  async DELETE_COMMENT({ commit }, params) {
    const boardSlug = params.boardSlug;
    const postId = params.postId;
    const commentId = params.commentId;
    const requestKey = `DELETE_COMMENT:${boardSlug}:${postId}:${commentId}`;

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.delete(`/api/v1/board/comment/${boardSlug}/${postId}/${commentId}`)
          .then((response) => {
            commit("DELETE_COMMENT", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, false); // DELETE 요청이므로 캐시 미적용
  },

  async LOAD_PHOTO_LIST({ commit }, params) {
    const boardId = params.boardId;
    const requestKey = `LOAD_PHOTO_LIST:${boardId}`;

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.get(`/api/v1/photo`)
          .then((response) => {
            commit("LOAD_PHOTO_LIST", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, true); // 사진 목록이므로 캐시 적용
  },

  async DELETE_PHOTO({ commit }, params) {
    const fileId = params.fileId;
    const requestKey = `DELETE_PHOTO:${fileId}`;

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.delete(`/api/v1/photo/${fileId}`)
          .then((response) => {
            commit("DELETE_PHOTO", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, false); // DELETE 요청이므로 캐시 미적용
  },

  async DELETE_PHOTO_LIST({ commit }, params) {
    const option = {
      fileIds: params.fileIds,
    };
    const requestKey = `DELETE_PHOTO_LIST:${JSON.stringify(params.fileIds)}`;

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.post("/api/v1/photo/delete", option)
          .then((response) => {
            commit("DELETE_PHOTO_LIST", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, false); // POST 요청이므로 캐시 미적용
  },

  /** 게시판 관련 첨부파일 목록 조회 */
  async LOAD_FILE_LIST({ commit }, params) {
    // params.boardIds[]
    const option = {
      params,
    };
    const requestKey = generateCacheKey('LOAD_FILE_LIST', params);

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.get(`/api/v1/board/attachment`, option)
          .then((response) => {
            commit("LOAD_FILE_LIST", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, true); // 파일 목록이므로 캐시 적용
  },

  /** 게시판 관련 첨부파일 추가 */
  async UPLOAD_FILE_LIST({ commit }, params) {
    const boardSlug = params.boardSlug;

    const option = {
      uploads: JSON.stringify(params.uploads),
    };
    const requestKey = `UPLOAD_FILE_LIST:${boardSlug}:${Date.now()}`;

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.post(`/api/v1/board/${boardSlug}/upload`, option)
          .then((response) => {
            commit("UPLOAD_FILE_LIST", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, false); // POST 요청이므로 캐시 미적용
  },

  /** 게시판 관련 첨부파일 제거 */
  async REMOVE_FILE_LIST({ commit }, params) {
    const boardSlug = params.boardSlug;

    const option = {
      fileIds: JSON.stringify(params.fileIds),
    };
    const requestKey = `REMOVE_FILE_LIST:${boardSlug}:${Date.now()}`;

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.put(`/api/v1/board/${boardSlug}/remove-attachment`, option)
          .then((response) => {
            // commit('UPLOAD_FILE_LIST', response)
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, false); // PUT 요청이므로 캐시 미적용
  },

  /** 게시판 관련 첨부파일 다운로드 */
  async DOWNLOAD_FILE({ commit }, params) {
    const boardSlug = params.boardSlug;
    const option = {
      fileId: params.fileId,
    };
    const requestKey = `DOWNLOAD_FILE:${boardSlug}:${params.fileId}`;

    return handleRequest(requestKey, () => {
      return new Promise((resolve, reject) => {
        HttpRequest.get(`/api/v1/board/${boardSlug}/download`, option)
          .then((response) => {
            commit("DOWNLOAD_FILE", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }, false); // 파일 다운로드는 캐시 미적용
  },
};
